/**
 * scrollWithin — bring something into view inside ONE scroller, and nowhere else.
 *
 * The studio is a page of zones that reveal each other: a row picked in the data
 * zone scrolls the focus phone to the card it renders, a module tapped in the
 * focus zone scrolls the data tree to its row, a tap in the live preview does
 * both. Every one of those is "scroll THIS box, leave everything else where the
 * reader put it".
 *
 * `el.scrollIntoView({ block: 'nearest' })` cannot express that. It walks EVERY
 * scrollable ancestor — the phone's page scroller, then the horizontal strip,
 * then the studio page itself — so revealing a card two screens down inside a
 * phone also jumped the whole studio, and the zone you were reading left the
 * viewport. Here the caller names the scroller and nothing above it moves.
 *
 * TRANSFORM-AWARE, because the phones are scaled. A `PhoneCard` renders a
 * 390×844 page under `transform: scale(0.558)`, so a box that is 100 CSS pixels
 * below the fold measures 55.8 SCREEN pixels below it — and `scrollTop` is in
 * the scroller's OWN pixels, not screen ones. Feeding it the screen delta
 * scrolled 44% short every time. The scale is read off the element itself
 * (`rect.height / clientHeight`), so it is right for any wrapper transform and
 * needs no import from `phoneMetrics`.
 *
 * jsdom has no layout: every rect is 0×0 and `scrollHeight === clientHeight === 0`.
 * That reads as "nothing is out of view", so every call is a silent no-op there
 * — never a throw — and the components that call this stay testable.
 */

/**
 * An element's box in SCREEN coordinates, falling back to the union of its
 * children. An inspect marker (`[data-vi-node]`) is `display: contents`: it
 * generates no box at all, so its own rect is 0×0 and only its children have
 * one — the same reason `FocusExplorer.moduleRect` measures the children.
 */
function screenRect(el) {
  if (!el?.getBoundingClientRect) return null;
  const r = el.getBoundingClientRect();
  if (r.width || r.height) return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
  let hit = null;
  for (const kid of el.children || []) {
    const k = kid.getBoundingClientRect?.();
    if (!k || (!k.width && !k.height)) continue;
    hit = hit ? {
      top: Math.min(hit.top, k.top), bottom: Math.max(hit.bottom, k.bottom),
      left: Math.min(hit.left, k.left), right: Math.max(hit.right, k.right),
    } : { top: k.top, bottom: k.bottom, left: k.left, right: k.right };
  }
  return hit;
}

/**
 * How far to scroll on one axis, in the SCROLLER's own pixels.
 *
 * `near` / `far` are the element's leading and trailing edges relative to the
 * scroller's client box, already divided by the scale. An element that overflows
 * BOTH ends is aligned to the leading edge — reading starts at the top of a card,
 * not at its end.
 */
function shift(near, far, pad) {
  if (near < pad) return near - pad;
  if (far > -pad) return far + pad;
  return 0;
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Move `scroller` the least amount that puts `el` inside its client box with
 * `pad` scroller-pixels to spare. Returns whether anything moved.
 *
 * Only `scroller.scrollTop` / `scrollLeft` are touched — no ancestor, no window.
 */
export function scrollWithin(scroller, el, pad = 10) {
  if (!scroller?.getBoundingClientRect || !el) return false;
  const box = scroller.getBoundingClientRect();
  const r = screenRect(el);
  // no layout (jsdom) or nothing rendered → there is no "out of view" to fix
  if (!r || (!box.width && !box.height)) return false;

  const sy = box.height && scroller.clientHeight ? box.height / scroller.clientHeight : 1;
  const sx = box.width && scroller.clientWidth ? box.width / scroller.clientWidth : 1;
  const dy = shift((r.top - box.top) / (sy || 1), (r.bottom - box.bottom) / (sy || 1), pad);
  const dx = shift((r.left - box.left) / (sx || 1), (r.right - box.right) / (sx || 1), pad);

  let moved = false;
  if (dy) {
    const max = Math.max(0, (scroller.scrollHeight || 0) - (scroller.clientHeight || 0));
    const next = clamp(Math.round((scroller.scrollTop || 0) + dy), 0, max);
    if (next !== scroller.scrollTop) { scroller.scrollTop = next; moved = true; }
  }
  if (dx) {
    const max = Math.max(0, (scroller.scrollWidth || 0) - (scroller.clientWidth || 0));
    const next = clamp(Math.round((scroller.scrollLeft || 0) + dx), 0, max);
    if (next !== scroller.scrollLeft) { scroller.scrollLeft = next; moved = true; }
  }
  return moved;
}

/** Can this element scroll, and is it asking to? */
function isScroller(el) {
  if (!el || el.nodeType !== 1) return false;
  // the runtime and the data columns both declare their scroller — that hook is
  // the intent, so trust it before measuring anything
  if (el.hasAttribute?.('data-vi-scroll')) return true;
  const canY = (el.scrollHeight || 0) - (el.clientHeight || 0) > 1;
  const canX = (el.scrollWidth || 0) - (el.clientWidth || 0) > 1;
  if (!canY && !canX) return false;
  const st = globalThis.getComputedStyle?.(el);
  if (!st) return false;
  const scrolls = (v) => v === 'auto' || v === 'scroll';
  return (canY && scrolls(st.overflowY)) || (canX && scrolls(st.overflowX));
}

/** The nearest scrollable ancestor of `el`, never leaving `root`. */
export function scrollerOf(el, root) {
  let cur = el?.parentElement || null;
  while (cur) {
    if (isScroller(cur)) return cur;
    if (cur === root) return null;
    cur = cur.parentElement;
  }
  return null;
}

/**
 * Reveal the module `nodeId` inside `root` — the phone card's frame — by
 * scrolling the app's OWN page scroller under it. Nothing outside `root` moves,
 * so the strip stays where it is and the studio page never jumps.
 */
export function scrollNodeIntoView(root, nodeId, pad = 10) {
  if (!root?.querySelector || !nodeId) return false;
  const esc = globalThis.CSS?.escape ? globalThis.CSS.escape(nodeId) : nodeId;
  let marker = null;
  try {
    marker = root.querySelector(`[data-vi-node="${esc}"]`);
  } catch {
    return false; // an id no selector can express — nothing to reveal
  }
  if (!marker) return false;
  return scrollWithin(scrollerOf(marker, root), marker, pad);
}
